import React, { useRef, useEffect, useState } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";

const ThreeJSModel = ({ image, text, height, weight, build }) => {
  const mountRef = useRef(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!mountRef.current) return;
    
    // Store the reference to the current mount element
    const currentMount = mountRef.current;
    
    // Clear previous renderer
    while (currentMount.firstChild) {
      currentMount.removeChild(currentMount.firstChild);
    }
    
    // Setup scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x111827); // Dark background color
    
    // Enhanced lighting for better visibility and aesthetics
    // Add ambient light
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    scene.add(ambientLight);
    
    // Add directional light with improved positioning
    const directionalLight = new THREE.DirectionalLight(0xffffff, 1.2);
    directionalLight.position.set(2, 3, 4);
    directionalLight.castShadow = true;
    directionalLight.shadow.mapSize.width = 1024;
    directionalLight.shadow.mapSize.height = 1024;
    scene.add(directionalLight);

    // Add a subtle purple point light for accent
    const pointLight = new THREE.PointLight(0x8b5cf6, 1.0, 12);
    pointLight.position.set(2, 1, 2);
    scene.add(pointLight);

    // Add a subtle blue point light for balance
    const blueLight = new THREE.PointLight(0x3b82f6, 0.8, 12);
    blueLight.position.set(-2, -1, 2);
    scene.add(blueLight);
    
    // Calculate container dimensions
    const containerWidth = currentMount.clientWidth;
    const containerHeight = currentMount.clientHeight;
    const aspectRatio = containerWidth / containerHeight;
    
    // Camera setup
    const camera = new THREE.PerspectiveCamera(50, aspectRatio, 0.1, 1000);
    camera.position.z = 3;
    
    // Renderer setup
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(containerWidth, containerHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    currentMount.appendChild(renderer.domElement);
    
    // Add orbit controls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.enableZoom = true;
    controls.autoRotate = true;
    controls.autoRotateSpeed = 1.0;
    
    // Create an improved t-shirt model
    const createTShirt = () => {
      // Calculate t-shirt dimensions based on user inputs
      const baseHeight = 1.5;
      const baseWidth = 1.0;
      
      // Scale factor based on height (180cm is our baseline)
      const heightFactor = height / 180;
      
      // Width adjustment based on weight and build
      let widthFactor = weight / 80; // 80kg is our baseline
      
      // Adjust based on build type
      switch (build) {
        case "lean":
          widthFactor *= 0.9;
          break;
        case "regular":
          widthFactor *= 1.0;
          break;
        case "athletic":
          widthFactor *= 1.1;
          break;
        case "big":
          widthFactor *= 1.2;
          break;
        default:
          break;
      }
      
      const tshirtHeight = baseHeight * heightFactor;
      const tshirtWidth = baseWidth * widthFactor;
      
      // Improved t-shirt geometry with more segments for better deformation
      const frontGeometry = new THREE.PlaneGeometry(tshirtWidth, tshirtHeight, 24, 24);
      const frontMaterial = new THREE.MeshPhysicalMaterial({ 
        color: 0xffffff,
        side: THREE.DoubleSide,
        map: null,
        roughness: 0.7,
        metalness: 0.1,
        reflectivity: 0.2,
        envMapIntensity: 0.3
      });
      
      // Improved cloth-like effect by adding subtle vertex displacement
      const vertices = frontGeometry.attributes.position.array;
      for (let i = 0; i < vertices.length; i += 3) {
        // Skip edges to maintain shape
        const x = vertices[i];
        const y = vertices[i + 1];
        
        // Only affect interior vertices for natural fabric folds
        if (Math.abs(x) < tshirtWidth * 0.45 && Math.abs(y) < tshirtHeight * 0.45) {
          // Add subtle random displacement for fabric texture effect
          vertices[i + 2] = (Math.random() - 0.5) * 0.03;
        }
      }
      frontGeometry.attributes.position.needsUpdate = true;
      frontGeometry.computeVertexNormals();
      
      const frontShirt = new THREE.Mesh(frontGeometry, frontMaterial);
      frontShirt.castShadow = true;
      frontShirt.receiveShadow = true;
      scene.add(frontShirt);
      
      // Create the back of the shirt with similar improvements
      const backGeometry = new THREE.PlaneGeometry(tshirtWidth, tshirtHeight, 24, 24);
      const backMaterial = new THREE.MeshPhysicalMaterial({ 
        color: 0xffffff,
        side: THREE.DoubleSide,
        roughness: 0.7,
        metalness: 0.1,
        reflectivity: 0.2,
        envMapIntensity: 0.3
      });
      
      // Add similar subtle displacement to back
      const backVertices = backGeometry.attributes.position.array;
      for (let i = 0; i < backVertices.length; i += 3) {
        const x = backVertices[i];
        const y = backVertices[i + 1];
        
        if (Math.abs(x) < tshirtWidth * 0.45 && Math.abs(y) < tshirtHeight * 0.45) {
          backVertices[i + 2] = (Math.random() - 0.5) * 0.03;
        }
      }
      backGeometry.attributes.position.needsUpdate = true;
      backGeometry.computeVertexNormals();
      
      const backShirt = new THREE.Mesh(backGeometry, backMaterial);
      backShirt.position.z = -0.05; // Offset the back slightly
      backShirt.rotation.y = Math.PI; // Rotate to face backward
      backShirt.castShadow = true;
      backShirt.receiveShadow = true;
      scene.add(backShirt);
      
      // Improved neckline instead of collar
      // Create a subtle curve for the neckline using a custom shape
      const neckShape = new THREE.Shape();
      const neckWidth = tshirtWidth * 0.2;
      const neckDepth = tshirtHeight * 0.08;
      
      // Draw a smooth curve for the neckline
      neckShape.moveTo(-neckWidth/2, tshirtHeight/2);
      neckShape.quadraticCurveTo(0, tshirtHeight/2 - neckDepth, neckWidth/2, tshirtHeight/2);
      
      const neckHole = new THREE.ShapeGeometry(neckShape);
      const neckEdgeMaterial = new THREE.MeshPhysicalMaterial({
        color: 0xf8f8f8,
        side: THREE.DoubleSide,
        roughness: 0.6,
        metalness: 0.1
      });
      
      const neckEdge = new THREE.Mesh(neckHole, neckEdgeMaterial);
      neckEdge.position.z = 0.01;
      neckEdge.castShadow = true;
      neckEdge.receiveShadow = true;
      scene.add(neckEdge);
      
      // Add same neckline to back
      const backNeckEdge = neckEdge.clone();
      backNeckEdge.position.z = -0.06;
      backNeckEdge.rotation.y = Math.PI;
      scene.add(backNeckEdge);
      
      // Improved sleeve design
      const sleeveGeometry = new THREE.PlaneGeometry(tshirtWidth * 0.3, tshirtHeight * 0.35, 12, 12);
      const sleeveMaterial = new THREE.MeshPhysicalMaterial({
        color: 0xffffff,
        side: THREE.DoubleSide,
        roughness: 0.7,
        metalness: 0.1,
        reflectivity: 0.2
      });
      
      // Add subtle sleeve folds
      const sleeveVertices = sleeveGeometry.attributes.position.array;
      for (let i = 0; i < sleeveVertices.length; i += 3) {
        // Add subtle wave pattern for sleeve folds
        const y = sleeveVertices[i + 1];
        sleeveVertices[i + 2] = Math.sin(y * 10) * 0.015;
      }
      sleeveGeometry.attributes.position.needsUpdate = true;
      sleeveGeometry.computeVertexNormals();
      
      // Left sleeve with improved positioning
      const leftSleeve = new THREE.Mesh(sleeveGeometry, sleeveMaterial);
      leftSleeve.position.set(-tshirtWidth/2 - tshirtWidth*0.12, tshirtHeight/4, 0);
      leftSleeve.rotation.z = Math.PI / 6; // Angle the sleeve outward
      leftSleeve.castShadow = true;
      leftSleeve.receiveShadow = true;
      scene.add(leftSleeve);
      
      // Right sleeve with improved positioning
      const rightSleeve = new THREE.Mesh(sleeveGeometry, sleeveMaterial);
      rightSleeve.position.set(tshirtWidth/2 + tshirtWidth*0.12, tshirtHeight/4, 0);
      rightSleeve.rotation.z = -Math.PI / 6; // Angle the sleeve outward
      rightSleeve.castShadow = true;
      rightSleeve.receiveShadow = true;
      scene.add(rightSleeve);
      
      // REMOVED: Shoulder curves section has been removed
      
      // Improved display platform
      const platformGeometry = new THREE.CylinderGeometry(1.3, 1.3, 0.05, 32);
      const platformMaterial = new THREE.MeshPhysicalMaterial({
        color: 0x1e1e2a,
        metalness: 0.8,
        roughness: 0.2,
        reflectivity: 0.7
      });
      const platform = new THREE.Mesh(platformGeometry, platformMaterial);
      platform.position.y = -1.2;
      platform.receiveShadow = true;
      scene.add(platform);
      
      // Enhanced glow ring around the platform
      const glowRingGeometry = new THREE.TorusGeometry(1.3, 0.04, 16, 64);
      const glowRingMaterial = new THREE.MeshBasicMaterial({
        color: 0x8b5cf6,
        transparent: true,
        opacity: 0.8
      });
      const glowRing = new THREE.Mesh(glowRingGeometry, glowRingMaterial);
      glowRing.position.y = -1.17;
      glowRing.rotation.x = Math.PI/2;
      scene.add(glowRing);
      
      // Return the front shirt plane that will receive the texture
      return frontShirt;
    };
    
    const shirtFront = createTShirt();
    
    // Update the t-shirt texture if an image is provided
    if (image) {
      const textureLoader = new THREE.TextureLoader();
      textureLoader.load(
        URL.createObjectURL(image),
        (texture) => {
          // Update the shirt texture
          shirtFront.material.map = texture;
          shirtFront.material.needsUpdate = true;
          setLoading(false);
        },
        undefined,
        (error) => {
          console.error("Error loading texture:", error);
          setLoading(false);
        }
      );
    } else {
      setLoading(false);
    }
    
    // Add text to the shirt if provided
    if (text) {
      // Create canvas for text
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.width = 512;
      canvas.height = 512;
      
      // Set transparent background
      context.fillStyle = 'rgba(0, 0, 0, 0)';
      context.fillRect(0, 0, canvas.width, canvas.height);
      
      // Improved text styles
      context.font = 'bold 48px Arial';
      context.textAlign = 'center';
      context.fillStyle = image ? 'white' : 'black'; // Use white text if there's an image background
      
      // Add text shadow for better visibility
      context.shadowColor = image ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.2)';
      context.shadowBlur = 5;
      context.shadowOffsetX = 2;
      context.shadowOffsetY = 2;
      
      // Split text into lines (max 3)
      const lines = text.split('\n', 3);
      lines.forEach((line, index) => {
        context.fillText(line, canvas.width / 2, canvas.height / 2 + (index - lines.length / 2) * 60);
      });
      
      // Create texture from canvas
      const textTexture = new THREE.CanvasTexture(canvas);
      
      // Create material with the text texture
      const textMaterial = new THREE.MeshBasicMaterial({
        map: textTexture,
        transparent: true
      });
      
      // Create a plane for the text
      const textGeometry = new THREE.PlaneGeometry(0.8, 0.8);
      const textMesh = new THREE.Mesh(textGeometry, textMaterial);
      textMesh.position.z = 0.01; // Slightly in front of shirt
      shirtFront.add(textMesh);
    }

    // Enhanced ambient particles for a premium effect
    const particlesGeometry = new THREE.BufferGeometry();
    const particlesCount = 300; // Increased particle count
    const posArray = new Float32Array(particlesCount * 3);
    
    for(let i = 0; i < particlesCount * 3; i++) {
      // Create a sphere distribution
      const angle1 = Math.random() * Math.PI * 2;
      const angle2 = Math.random() * Math.PI * 2;
      const radius = 3 + Math.random() * 3; // Increased radius range
      
      posArray[i] = Math.cos(angle1) * Math.sin(angle2) * radius;
      posArray[i+1] = Math.sin(angle1) * Math.sin(angle2) * radius;
      posArray[i+2] = Math.cos(angle2) * radius;
      i += 2;
    }
    
    particlesGeometry.setAttribute('position', new THREE.BufferAttribute(posArray, 3));
    
    // Improved particle appearance
    const particlesMaterial = new THREE.PointsMaterial({
      size: 0.015,
      color: 0xffffff,
      transparent: true,
      opacity: 0.5,
      depthWrite: false
    });
    
    const particlesMesh = new THREE.Points(particlesGeometry, particlesMaterial);
    scene.add(particlesMesh);
    
    // Animation loop with improved effects
    const animate = () => {
      requestAnimationFrame(animate);
      
      // Rotate particles with subtle speed variation
      particlesMesh.rotation.y += 0.0007;
      particlesMesh.rotation.x += 0.0003;
      
      // Pulsate the glow ring with improved effect
      const time = Date.now() * 0.001;
      
      // Only apply effects to the platform glow ring, not to any shoulder curves
      scene.children.forEach(child => {
        // Special effect for the glow ring at the bottom platform
        if (child.geometry && child.geometry.type === "TorusGeometry" && child.position.y === -1.17) {
          // Pulsating effect
          const pulseFactor = 0.7 + Math.sin(time * 2) * 0.3;
          child.material.opacity = pulseFactor;
          child.scale.set(1 + Math.sin(time) * 0.05, 1 + Math.sin(time) * 0.05, 1);
        }
      });
      
      controls.update();
      renderer.render(scene, camera);
    };
    
    animate();
    
    // Handle window resize
    const handleResize = () => {
      if (currentMount) {
        const width = currentMount.clientWidth;
        const height = currentMount.clientHeight;
        
        camera.aspect = width / height;
        camera.updateProjectionMatrix();
        renderer.setSize(width, height);
      }
    };
    
    window.addEventListener('resize', handleResize);
    
    // Clean up
    return () => {
      window.removeEventListener('resize', handleResize);
      if (currentMount && renderer.domElement) {
        currentMount.removeChild(renderer.domElement);
      }
    };
  }, [image, text, height, weight, build]);
  
  return (
    <div className="w-full h-full relative">
      <div ref={mountRef} className="w-full h-full rounded-md"></div>
      {loading && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-900/80 rounded-md">
          <div className="w-10 h-10 border-t-2 border-b-2 border-purple-500 rounded-full animate-spin mb-3"></div>
          <div className="text-gray-300">Loading 3D Model...</div>
        </div>
      )}
    </div>
  );
};

export default ThreeJSModel;
